import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {CreditsModalContent} from '@Components/ai-chat-window/components/credits-modal-content/credits-modal-content';
import {openMoreOptionsModal} from '@Modals/more-options-modal/more-options-modal-library';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {CreditsSummaryPopup} from './components/credits-summary-popup/credits-summary-popup';
import type {AiChatCreditsDropdownProps} from './ai-chat-credits-dropdown.types';
import {CREDITS_DROPDOWN_HOVER_CLOSE_DELAY_MS, CREDITS_MODAL_PANEL_ID, MODAL_PANEL_PADDING_CLASS} from './ai-chat-credits-dropdown.types';
import styles from './ai-chat-credits-dropdown.module.scss';

export function AiChatCreditsDropdown({selector, alignment = ALIGNMENT_TYPE.BOTTOM_END}: AiChatCreditsDropdownProps): ReactElement {
  const {state, actions} = useAiChat();
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MOBILE_MODAL_ACTIVATION_BREAKPONT);

  const cancelScheduledClose = (): void => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = (): void => {
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, CREDITS_DROPDOWN_HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return (): void => {
      cancelScheduledClose();
    };
  }, []);

  const close = (): void => {
    setIsOpen(false);
  };

  const openOnHover = (): void => {
    cancelScheduledClose();
    setIsOpen(true);
  };

  const toggle = (): void => {
    setIsOpen(!isOpen);
  };

  const openCreditsSummaryModal = (): void => {
    openMoreOptionsModal({
      headerTitle: window.i18next.t('pmwjs_ai_credits_summary'),
      content: <CreditsModalContent>{getPopup()}</CreditsModalContent>,
      panelId: CREDITS_MODAL_PANEL_ID,
      className: MODAL_PANEL_PADDING_CLASS,
      shouldCenterAlignTitle: true,
      showCloseButton: true,
    });
  };

  const getPopup = (): ReactElement => {
    return <CreditsSummaryPopup credits={state.credits} mode={state.mode} isMobile={isMobile} onHoverStart={cancelScheduledClose} onHoverEnd={scheduleClose} onActionTaken={close} onCreditsPurchased={actions.refreshCredits} onOpenSummaryModal={openCreditsSummaryModal} />;
  };

  const getHoverSelector = (): ReactElement => {
    return (
      <ClickableDiv onMouseEnter={openOnHover} onMouseLeave={scheduleClose} onClick={toggle}>
        {selector}
      </ClickableDiv>
    );
  };

  if (isMobile) {
    return <ClickableDiv onClick={openCreditsSummaryModal}>{selector}</ClickableDiv>;
  }

  return <Dropdown open={isOpen} selector={getHoverSelector()} popup={getPopup()} alignment={alignment} popUpHasCustomWidth popupClassName={styles.creditsDropdownPopup} onClose={close} />;
}
